import React, { useMemo, useRef, useState } from "react";
import { Empty } from "antd";

export const getNoDataMessage = (dataName) =>
  dataName
    ? `No ${dataName} data available.`
    : "No data found.";

export const getNoDataStatus = (chartData = {}, chartInstance = null) => {
  const datasets = Array.isArray(chartData?.datasets) ? chartData.datasets : [];
  const hasFiniteValue = datasets.some((dataset) =>
    (!chartInstance || chartInstance.isDatasetVisible?.(datasets.indexOf(dataset))) &&
    Array.isArray(dataset?.data) &&
    dataset.data.some((value) => {
      if (value === null || value === undefined || value === "") return false;
      const numericValue = typeof value === "number" ? value : Number(value);
      return Number.isFinite(numericValue);
    }),
  );
  const hasExplicitNoData = Boolean(
    chartData?.noData || datasets.some((dataset) => dataset?.noData),
  );
  return {
    hasData: !hasExplicitNoData && hasFiniteValue,
  };
};

export const NoDataMessageContent = ({ dataName }) => {
  const resolvedMessage = getNoDataMessage(dataName);

  return (
    <div
      style={{
        width: "min(100%, 620px)",
        fontSize: "clamp(11px, 1.8vw, 14px)",
        fontWeight: 600,
        lineHeight: 1.4,
        overflowWrap: "anywhere",
      }}
    >
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={false} />
      <div>{resolvedMessage}</div>
    </div>
  );
};

const EmptyChart = ({ chartData, children, dataName }) => {
  const chartInstanceRef = useRef(null);
  const visibilitySignatureRef = useRef("");
  const [, setVisibilityVersion] = useState(0);

  const visibilityPlugin = useMemo(() => ({
    id: "emptyChartVisibility",
    afterUpdate: (chart) => {
      const signature = (chart.data?.datasets || [])
        .map((_, index) => chart.isDatasetVisible(index))
        .join(",");

      if (signature !== visibilitySignatureRef.current) {
        visibilitySignatureRef.current = signature;
        chartInstanceRef.current = chart;
        setVisibilityVersion((version) => version + 1);
      }
    },
  }), []);

  const child = React.isValidElement(children)
    ? React.cloneElement(children, {
        plugins: [...(children.props.plugins || []), visibilityPlugin],
        ref: (instance) => {
          chartInstanceRef.current = instance;
          if (typeof children.ref === "function") {
            children.ref(instance);
          } else if (children.ref) {
            children.ref.current = instance;
          }
        },
      })
    : children;

  const { hasData } = getNoDataStatus(chartData, chartInstanceRef.current);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      {child}
      {!hasData && (
        <div
          className="no-data-chart-message"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxSizing: "border-box",
            padding: "16px",
            color: "#8c8c8c",
            fontFamily: "Inter, sans-serif",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <NoDataMessageContent dataName={dataName} />
        </div>
      )}
    </div>
  );
};

export default EmptyChart;